import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';
import { fmtDate } from '../../dateUtils';

const DEFAULT_POS = {
  paperWidth: '80', fontFamily: 'Courier New',
  headerSize: 16, bodySize: 13, footerSize: 11,
  headerAlign: 'center', divider: 'dashed',
  boldHotelName: true, boldTotals: true,
  showAddress: true, showPhone: true, showEmail: false, showGst: true,
  showTableNo: true, showOrderNo: true, showDate: true,
};

function printBill(data) {
  const hi   = data.hotelInfo || {};
  const bill = data.bill      || {};
  const items = data.items    || [];

  let pos = DEFAULT_POS;
  if (hi.pos_config) {
    try { pos = { ...DEFAULT_POS, ...JSON.parse(hi.pos_config) }; } catch (_) {}
  }

  const hSize    = parseInt(pos.headerSize) || 16;
  const bSize    = parseInt(pos.bodySize)   || 13;
  const fSize    = parseInt(pos.footerSize) || 11;
  const maxWidth = pos.paperWidth === '58' ? 340 : 420;
  const divStyle = pos.divider === 'dashed' ? '1px dashed #000'
                 : pos.divider === 'solid'  ? '1px solid #000' : 'none';
  const divHtml  = pos.divider !== 'none'
    ? `<div style="border-top:${divStyle};margin:6px 0"></div>`
    : `<div style="margin:4px 0"></div>`;

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const now = new Date().toLocaleDateString('en-IN') + ' '
            + new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const itemRows = items.map(i => `
    <tr>
      <td style="padding:3px 4px;border-bottom:1px solid #eee">${i.item_name}</td>
      <td style="padding:3px 4px;border-bottom:1px solid #eee;text-align:center">${i.quantity}</td>
      <td style="padding:3px 4px;border-bottom:1px solid #eee;text-align:right">${fmtRs(i.price)}</td>
      <td style="padding:3px 4px;border-bottom:1px solid #eee;text-align:right;font-weight:600">${fmtRs(i.price * i.quantity)}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Bill ${bill.bill_no || ''}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body {
    font-family: '${pos.fontFamily}', 'Courier New', monospace;
    font-size: ${bSize}px; color: #000;
    max-width: ${maxWidth}px; margin: 0 auto; padding: 16px 8px;
  }
  .hotel-name {
    font-size: ${hSize}px; font-weight: ${pos.boldHotelName ? 800 : 600};
    text-align: ${pos.headerAlign}; margin-bottom: 3px;
  }
  .subheader { text-align: ${pos.headerAlign}; font-size: ${fSize}px; color: #444; }
  .header-text { text-align: center; color: #1a5c31; font-size: ${fSize}px;
                 white-space: pre-line; margin: 4px 0; }
  .footer-text { text-align: center; font-size: ${fSize}px; color: #555;
                 white-space: pre-line; }
  .row  { display: flex; justify-content: space-between; margin-bottom: 2px; }
  .total-row {
    display: flex; justify-content: space-between;
    font-size: ${Math.round(bSize * 1.1)}px;
    font-weight: ${pos.boldTotals ? 800 : 600};
    padding-top: 5px; border-top: 2px solid #000; margin-top: 4px;
  }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f0f0f0; padding: 4px 4px; font-size: ${bSize - 1}px; text-align: left; }
  th:nth-child(2) { text-align: center; }
  th:nth-child(3), th:nth-child(4) { text-align: right; }
  @media print {
    body { max-width: 100%; padding: 0; }
    @page { margin: 4mm; size: ${pos.paperWidth}mm auto; }
  }
</style>
</head>
<body>
  <div class="hotel-name">${hi.hotel_name || 'Hotel'}</div>
  ${pos.showAddress && hi.address ? `<div class="subheader">${hi.address}</div>` : ''}
  ${pos.showPhone   && hi.phone   ? `<div class="subheader">&#9990; ${hi.phone}</div>` : ''}
  ${pos.showEmail   && hi.email   ? `<div class="subheader">&#9993; ${hi.email}</div>` : ''}
  ${pos.showGst     && hi.gst_no  ? `<div class="subheader">GST: ${hi.gst_no}</div>` : ''}
  ${hi.bill_header  ? `${divHtml}<div class="header-text">${hi.bill_header}</div>` : ''}
  ${divHtml}
  <div class="row" style="font-size:${fSize}px;color:#555">
    ${pos.showOrderNo ? `<span><b>Bill:</b> ${bill.bill_no || '—'}</span>` : '<span></span>'}
    ${pos.showTableNo ? `<span><b>Table:</b> ${data.table_no || '—'}</span>` : ''}
  </div>
  ${pos.showDate ? `<div style="font-size:${fSize}px;color:#555">${now}</div>` : ''}
  ${divHtml}
  <table>
    <thead><tr>
      <th>Item</th>
      <th style="text-align:center">Qty</th>
      <th style="text-align:right">Rate</th>
      <th style="text-align:right">Amt</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  ${divHtml}
  <div class="row"><span>Subtotal</span><span>${fmtRs(bill.subtotal)}</span></div>
  ${bill.discount > 0 ? `<div class="row" style="color:#c00"><span>Discount</span><span>-${fmtRs(bill.discount)}</span></div>` : ''}
  <div class="row"><span>Tax (${bill.tax_pct}%)</span><span>${fmtRs(bill.tax_amount)}</span></div>
  <div class="total-row"><span>TOTAL</span><span>${fmtRs(bill.total)}</span></div>
  ${bill.payment_mode ? `${divHtml}<div class="row"><span>Payment</span><span>${bill.payment_mode}</span></div>` : ''}
  ${hi.bill_footer ? `${divHtml}<div class="footer-text">${hi.bill_footer}</div>` : ''}
  ${divHtml}
  <div style="text-align:center;font-size:10px;color:#aaa;margin-top:4px">Powered by SmaBizZ</div>
</body>
</html>`;

  const w = window.open('', '_blank', `width=${maxWidth + 40},height=700`);
  if (!w) { alert('Please allow popups to print the bill.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.onload = () => { w.focus(); w.print(); };
  setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 600);
}

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Bank Transfer', 'Other'];

function BillCard({ order, onRefresh, autoExpand }) {
  const [expanded, setExpanded]     = useState(false);
  const [billData, setBillData]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [paying, setPaying]         = useState(false);
  const [payMode, setPayMode]       = useState('Cash');
  const [discount, setDiscount]     = useState(0);
  const [taxPct, setTaxPct]         = useState('');

  const loadBill = async (forceExpand) => {
    if (!expanded || forceExpand) {
      setLoading(true);
      try {
        const data = await hotelApi.getBilling(order.id);
        setBillData(data);
        // Pre-fill tax from hotel info
        if (data?.hotelInfo?.tax_pct != null) setTaxPct(String(data.hotelInfo.tax_pct));
      } finally { setLoading(false); }
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (autoExpand) loadBill(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = { discount: parseFloat(discount) || 0 };
      if (taxPct !== '') payload.taxPct = parseFloat(taxPct);
      await hotelApi.generateBill(order.id, payload);
      const data = await hotelApi.getBilling(order.id);
      setBillData(data);
    } finally { setGenerating(false); }
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      await hotelApi.payBill(order.id, { paymentMode: payMode });
      onRefresh();
    } finally { setPaying(false); }
  };

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div onClick={loadBill}
        style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', background: '#f8faf8',
          borderRadius: expanded ? '12px 12px 0 0' : 12 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#0d3321' }}>
            Table {order.table_no || '—'}
          </span>
          {order.section && <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>{order.section}</span>}
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#888', marginLeft: 10 }}>
            #{order.order_no}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, color: '#137333', fontSize: 16 }}>{fmtRs(order.subtotal)}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{order.item_count} items</div>
          </div>
          <span style={{ color: '#888', fontSize: 18 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 18px 16px' }}>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Loading…</div>}
          {billData && (
            <div>
              {/* Item list */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f1f3f4' }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>Item</th>
                    <th style={{ padding: '6px 10px', textAlign: 'center' }}>Qty</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right' }}>Price</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(billData.items || []).map(item => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '7px 10px' }}>{item.item_name}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtRs(item.price)}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>
                        {fmtRs(item.price * item.quantity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Bill summary */}
              {billData.bill ? (
                <div style={{ marginTop: 16, background: '#f8faf8', borderRadius: 10,
                  padding: '14px 16px', borderLeft: '4px solid #137333' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}>
                    <span>Subtotal</span><span>{fmtRs(billData.bill.subtotal)}</span>
                  </div>
                  {billData.bill.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: '#c5221f' }}>
                      <span>Discount</span><span>−{fmtRs(billData.bill.discount)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}>
                    <span>Tax ({billData.bill.tax_pct}%)</span><span>{fmtRs(billData.bill.tax_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800,
                    fontSize: 18, borderTop: '2px solid #e8eaed', paddingTop: 8, marginTop: 4 }}>
                    <span>TOTAL</span>
                    <span style={{ color: '#137333' }}>{fmtRs(billData.bill.total)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={payMode} onChange={e => setPayMode(e.target.value)}
                      className="form-control" style={{ maxWidth: 160, fontSize: 13 }}>
                      {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <button onClick={handlePay} disabled={paying}
                      style={{ background: '#137333', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                      {paying ? 'Processing…' : '✅ Mark Paid'}
                    </button>
                    <button onClick={() => printBill(billData)}
                      style={{ background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                      🖨️ Print Bill
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Discount (₹)</div>
                      <input type="number" value={discount} onChange={e => setDiscount(e.target.value)}
                        placeholder="0" className="form-control" style={{ width: 110, fontSize: 13 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Tax %</div>
                      <input type="number" value={taxPct} onChange={e => setTaxPct(e.target.value)}
                        placeholder={billData?.hotelInfo?.tax_pct ?? '5'}
                        className="form-control" style={{ width: 90, fontSize: 13 }} />
                    </div>
                    <div style={{ marginTop: 18 }}>
                      <button onClick={handleGenerate} disabled={generating}
                        style={{ background: '#1a4731', color: '#fff', border: 'none', borderRadius: 8,
                          padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                        {generating ? 'Generating…' : '🧾 Generate Bill'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HotelBilling({ role, initialOrderId, onMount }) {
  const [tab, setTab]         = useState('open');
  const [openOrders, setOpenOrders] = useState([]);
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => { if (onMount) onMount(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    setLoading(true);
    const promises = tab === 'open'
      ? [hotelApi.getOpenBilling()]
      : [Promise.resolve([]), hotelApi.getBillingHistory()];
    Promise.all(promises)
      .then(([open, hist]) => {
        if (open) setOpenOrders(open);
        if (hist) setHistory(hist);
      })
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'open' ? ' active' : ''}`} onClick={() => setTab('open')}>
          🧾 Open Bills {!loading && tab === 'open' && `(${openOrders.length})`}
        </button>
        <button className={`tab${tab === 'history' ? ' active' : ''}`} onClick={() => setTab('history')}>
          📜 History
        </button>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>}

      {!loading && tab === 'open' && (
        <div>
          {openOrders.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: 48 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#137333', marginTop: 12 }}>
                No pending bills
              </div>
            </div>
          )}
          {openOrders.map(o => (
            <BillCard key={o.id} order={o} onRefresh={load}
              autoExpand={initialOrderId && String(o.id) === String(initialOrderId)} />
          ))}
        </div>
      )}

      {!loading && tab === 'history' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Payment History</div>
            <button className="btn btn-secondary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={load}>↻ Refresh</button>
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Bill No</th>
                  <th>Table</th>
                  <th>Order</th>
                  <th>Subtotal</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Mode</th>
                  <th>Paid At</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                    No payment history yet
                  </td></tr>
                )}
                {history.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.bill_no}</td>
                    <td>{b.table_no || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.order_no}</td>
                    <td>{fmtRs(b.subtotal)}</td>
                    <td>{fmtRs(b.tax_amount)}</td>
                    <td style={{ fontWeight: 700, color: '#137333' }}>{fmtRs(b.total)}</td>
                    <td>
                      <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 10,
                        padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                        {b.payment_mode}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#888' }}>{fmtDate(b.paid_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
